import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { challengesAPI } from '../../lib/api';
import api from '../../lib/api';
import { Users, Briefcase, Loader2, Trophy, ArrowLeft } from 'lucide-react';

export const JudgeProfilePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await challengesAPI.getJudgeProfile(slug);
        setProfile(res.data);
        // Pick the most recent challenge to infer event context
        const ch = (res.data.challenges || [])[0];
        if (ch?.event_id) {
          try {
            const evRes = await api.get(`/events/${ch.event_id}`);
            setEventInfo(evRes.data);
          } catch { /* ignore */ }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Giurato non trovato</div>;

  const { judge, challenges: allChallenges = [] } = profile;
  const challenges = allChallenges.filter(c => !c.parent_challenge_id);
  const backTarget = challenges[0]
    ? { to: `/challenges/${challenges[0].slug || challenges[0].id}`, label: `Torna a ${challenges[0].title}` }
    : eventInfo
      ? { to: `/events/${eventInfo.slug || eventInfo.id}`, label: `Torna a ${eventInfo.title}` }
      : null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="judge-profile-page">
      <div className="max-w-3xl mx-auto space-y-8">
        {backTarget && (
          <button
            onClick={() => navigate(backTarget.to)}
            className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 transition-colors"
            data-testid="back-from-judge-btn"
          >
            <ArrowLeft size={14} /> {backTarget.label}
          </button>
        )}

        {/* Pill role */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25" data-testid="judge-role-pill">
            <Trophy size={16} />
            <span className="text-xs font-mono uppercase tracking-[0.3em] font-bold">Profilo Giurato</span>
          </div>
        </div>

        {/* Judge card */}
        <div className="glass rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6">
          {judge.image ? (
            <img
              src={judge.image.startsWith('http') ? judge.image : `${process.env.REACT_APP_BACKEND_URL}${judge.image}`}
              alt={`${judge.first_name} ${judge.last_name}`}
              className="w-40 h-40 rounded-full object-cover flex-shrink-0 ring-2 ring-primary/30"
            />
          ) : (
            <div className="w-40 h-40 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 ring-2 ring-primary/30">
              <Users size={56} className="text-primary" />
            </div>
          )}
          <div className="text-center md:text-left flex-1">
            <h2 className="font-heading text-2xl md:text-3xl font-bold">{judge.first_name} {judge.last_name}</h2>
            {judge.job_title && (
              <p className="text-primary mt-1.5 font-medium inline-flex items-center gap-2 justify-center md:justify-start">
                <Briefcase size={16} /> {judge.job_title}
              </p>
            )}
            {judge.bio && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{judge.bio}</p>}
          </div>
        </div>

        {/* Judged challenges (parent only) below card */}
        {challenges.length > 0 && (
          <div className="text-center space-y-3 pt-10" data-testid="judge-challenges-section">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">Membro della giuria di</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {challenges.map((ch, idx) => (
                <span key={ch.id} className="inline-flex items-center">
                  <Link
                    to={`/challenges/${ch.slug || ch.id}`}
                    className="font-heading text-xl md:text-2xl font-bold text-primary hover:underline"
                  >
                    {ch.title}
                  </Link>
                  {idx < challenges.length - 1 && <span className="mx-2 text-muted-foreground">·</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
